import { clsx } from 'clsx'
import type { ReactNode, ButtonHTMLAttributes } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  color?: 'primary' | 'secondary' | 'danger'
  plain?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const variantClasses = {
  primary: 'btn-filled-primary',
  secondary: 'btn-filled-secondary',
  danger: 'btn-filled-danger',
  ghost: 'btn-ghost',
}

const sizeClasses = {
  sm: 'btn-sm',
  md: 'btn-md',
  lg: 'btn-lg',
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={clsx(
        'btn-foundation btn-ripple',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      disabled={disabled}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  )
}

const iconButtonColors = {
  primary: {
    solid: 'btn-filled-primary',
    plain: 'btn-icon-plain-primary',
  },
  secondary: {
    solid: 'btn-filled-secondary',
    plain: 'btn-icon-plain-secondary',
  },
  danger: {
    solid: 'btn-filled-danger',
    plain: 'btn-icon-plain-danger',
  },
}

export function IconButton({
  children,
  color = 'secondary',
  plain = false,
  size = 'md',
  className,
  disabled,
  ...props
}: IconButtonProps) {
  return (
    <button
      className={clsx(
        'btn-icon-foundation',
        iconButtonColors[color][plain ? 'plain' : 'solid'],
        size === 'sm' && 'min-w-[40px] min-h-[40px]',
        size === 'lg' && 'min-w-[48px] min-h-[48px]',
        className
      )}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  )
}
